import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

export default function VidaEmpresarialPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Seguro de Vida Empresarial",
    "provider": {
      "@type": "Organization",
      "name": "IBX Brasil"
    },
    "description": "Consultoria especializada em seguros de vida empresarial para proteção de sócios e colaboradores com análise de coberturas e gestão estratégica.",
    "serviceType": "Consultoria em Seguros de Vida Empresarial",
    "areaServed": "BR"
  };

  return (
    <>
      <SEOHead
        title="Seguro de Vida Empresarial | Proteção para Sócios e Colaboradores"
        description="Proteja sócios e colaboradores com consultoria especializada em seguros de vida empresarial. Análise de coberturas, planejamento sucessório e gestão estratégica de apólices."
        canonical="https://ibxbrasil.com.br/servicos/vida-empresarial"
        ogImage="https://readdy.ai/api/search-image?query=professional%20business%20team%20meeting%20discussing%20corporate%20life%20insurance%20protection%20plans%20modern%20office%20boardroom%20handshake%20agreement%20professional%20atmosphere%20clean%20background%20realistic%20corporate%20photography&width=1200&height=630&seq=seo-vida-empresarial-service&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="SERVIÇOS • VIDA EMPRESARIAL"
          title="Seguro de Vida Empresarial Estratégico"
          highlightedWord="Vida"
          highlightColor="green"
          subtitle="Estruturação completa de capital segurado, planejamento sucessório e proteção financeira empresarial com análise técnica especializada."
          benefits={[
            'Capital segurado dimensionado corretamente',
            'Planejamento sucessório empresarial',
            'Revisão contratual detalhada',
            'Proteção financeira estratégica'
          ]}
          ctaText="Falar no WhatsApp"
          ctaLink="https://wa.me/5577988080505?text=Olá! Gostaria de saber mais sobre seguro de vida empresarial."
          ctaSecondary={{
            text: 'Solicitar Análise',
            link: '#contato'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20business%20life%20insurance%20planning%20meeting%20with%20executives%20in%20modern%20corporate%20office%20elegant%20atmosphere%20strategic%20planning%20documents%20financial%20security%20concept&width=1920&height=700&seq=vida-empresarial-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Why Important Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Por Que é Estratégico para Empresários?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Proteção financeira que vai além do seguro de vida tradicional
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Continuidade do Negócio</h3>
                <p className="text-gray-600 leading-relaxed">
                  Garante recursos financeiros para manter a operação da empresa em caso de falecimento ou invalidez de sócios e executivos-chave.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-exchange-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Sucessão Planejada</h3>
                <p className="text-gray-600 leading-relaxed">
                  Estrutura financeira para aquisição de quotas societárias e transição patrimonial sem comprometer o caixa da empresa.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Proteção Patrimonial</h3>
                <p className="text-gray-600 leading-relaxed">
                  Blindagem do patrimônio pessoal e empresarial com estruturação técnica de coberturas e beneficiários.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Analysis */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Análise Financeira Completa
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Avaliação técnica de riscos e dimensionamento de coberturas
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <i className="ri-line-chart-line text-2xl text-orange-600"></i>
                  Avaliação de Riscos Financeiros
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Análise de endividamento empresarial e pessoal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Mapeamento de obrigações financeiras de curto e longo prazo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Avaliação de dependência financeira de terceiros</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Projeção de necessidades futuras da família e empresa</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <i className="ri-calculator-line text-2xl text-orange-600"></i>
                  Dimensionamento de Capitais
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Cálculo técnico de capital segurado necessário</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Estruturação de coberturas por morte e invalidez</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Definição de coberturas complementares estratégicas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Análise de custo-benefício de cada cobertura</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <i className="ri-team-line text-2xl text-orange-600"></i>
                  Estruturação de Beneficiários
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Definição estratégica de beneficiários pessoa física e jurídica</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Percentuais de distribuição conforme planejamento sucessório</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Análise de implicações tributárias e sucessórias</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Revisão periódica conforme mudanças societárias</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 border-l-4 border-orange-600">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
                  Negociação Especializada
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Cotação com seguradoras especializadas em vida empresarial</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Análise comparativa de condições contratuais</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Negociação de prêmios e carências</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-orange-600 text-lg mt-0.5"></i>
                    <span className="text-gray-600">Garantia das melhores condições do mercado</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Situações Estratégicas de Aplicação
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Quando a avaliação financeira de vida empresarial é fundamental
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-building-4-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Sociedades Empresariais</h3>
                <p className="text-gray-600 text-sm">Proteção para aquisição de quotas em caso de falecimento de sócio.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-user-star-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Executivos-Chave</h3>
                <p className="text-gray-600 text-sm">Cobertura para profissionais essenciais à operação da empresa.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-bank-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Garantia de Empréstimos</h3>
                <p className="text-gray-600 text-sm">Quitação de dívidas bancárias e financiamentos empresariais.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-parent-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Proteção Familiar</h3>
                <p className="text-gray-600 text-sm">Manutenção do padrão de vida da família do empresário.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-exchange-funds-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Planejamento Sucessório</h3>
                <p className="text-gray-600 text-sm">Recursos para transição patrimonial sem impacto no caixa.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
                <i className="ri-shield-star-line text-3xl text-orange-600 mb-3"></i>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Blindagem Patrimonial</h3>
                <p className="text-gray-600 text-sm">Proteção de bens pessoais e empresariais contra riscos.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Proteja Sua Equipe com Segurança e Tranquilidade
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Solicite uma avaliação financeira gratuita e estruture a proteção estratégica da sua empresa
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Cotação
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}